import React, { useState, useEffect } from "react";
import checkPermission from "../Utils/checkPermission";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from "@mui/material";
import { Add as AddIcon, CheckCircle, Cancel } from "@mui/icons-material";
import HolidayFormDialog from "./HolidayFormDialog";
import DeleteConfirmDialog from "../Utils/DeleteConfirmDialog";
import axiosApi from "../Utils/axiosApi";

const HolidayDialog = ({
  openDialog,
  handleCloseHoliday,
  holidays = [],
  onSave,
  hostelId,
  hostelName,
}) => {
  const [holidayList, setHolidayList] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    setHolidayList(holidays.map((h) => normalizeHoliday(h)));
  }, [holidays]);

  const normalizeHoliday = (holiday) => {
    if (!holiday) return holiday;
    return {
      ...holiday,
      scheduleType: holiday.scheduleType || "-",
      stayClosed:
        holiday.stayClosed === "true" ||
        holiday.stayClosed === true ||
        holiday.stayClosed === "Y"
          ? "Y"
          : "N",
      foodNotAvailable:
        holiday.foodClosed === "true" ||
        holiday.foodClosed === true ||
        holiday.foodNotAvailable === "Y"
          ? "Y"
          : "N",
      startDate: holiday.startDate ? holiday.startDate.substring(0, 10) : "",
      endDate: holiday.endDate ? holiday.endDate.substring(0, 10) : "",
    };
  };

  const handleAdd = () => {
    setEditingHoliday(null);
    setFormOpen(true);
  };

  const handleEdit = async (holiday) => {
    try {
      const response = await axiosApi.post(
        "/hostelHolidayOrClosure/getHostelHolidayOrClosure",
        { hostelHolidayOrClosureIds: [holiday.id] }
      );

      if (response.status === 200 && response.data?.length > 0) {
        setEditingHoliday(normalizeHoliday(response.data[0]));
      } else {
        setEditingHoliday(normalizeHoliday(holiday));
      }
    } catch (err) {
      console.error("Error fetching holiday:", err);
      setEditingHoliday(normalizeHoliday(holiday));
    }
    setFormOpen(true);
  };

  const handleFormSave = (holiday) => {
    const normalized = normalizeHoliday({
      ...holiday,
      hostelName:
        holiday.hostelName ||
        holidayList.find((h) => h.id === holiday.id)?.hostelName ||
        hostelName ||
        "-",
    });

    let updated;
    if (editingHoliday) {
      updated = holidayList.map((h) =>
        h.id === normalized.id ? { ...h, ...normalized } : h
      );
    } else {
      updated = [...holidayList, normalized];
    }

    setHolidayList(updated);
    if (onSave) onSave(updated);
  };

  const askDelete = (holiday) => {
    setDeleteTarget(holiday);
    setDeleteOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget?.id) {
      setDeleteOpen(false);
      return;
    }

    try {
      await axiosApi.post(
        "/hostelHolidayOrClosure/deleteHostelHolidayOrClosure",
        { hostelHolidayOrClosureIds: [deleteTarget.id] }
      );

      const updated = holidayList.filter((h) => h.id !== deleteTarget.id);
      setHolidayList(updated);
      if (onSave) onSave(updated);
    } catch (err) {
      console.error("Failed to delete:", err);
    } finally {
      setDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Dialog
      open={openDialog}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") handleCloseHoliday();
      }}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>Manage Hostel Holidays / Closures</DialogTitle>

      <DialogContent dividers>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Hostel Name</TableCell>
              <TableCell>Schedule Type</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell align="center">Stay Closed</TableCell>
              <TableCell align="center">No Food</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {holidayList.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No holidays added yet.
                </TableCell>
              </TableRow>
            )}

            {holidayList.map((holiday, index) => (
              <TableRow key={index}>
                <TableCell>{holiday.hostelName || "-"}</TableCell>
                <TableCell>{holiday.scheduleType}</TableCell>
                <TableCell>{holiday.startDate || "-"}</TableCell>
                <TableCell>{holiday.endDate || "-"}</TableCell>
                <TableCell align="center">
                  {holiday.stayClosed === "Y" ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <Cancel color="error" fontSize="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  {holiday.foodNotAvailable === "Y" ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <Cancel color="error" fontSize="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleEdit(holiday)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => askDelete(holiday)}
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions>
        <Button
          disabled={
            !checkPermission("Hostel", "Hostel List", "CREATEHOLIDAYDETAIL")
          }
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add Holiday
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (onSave) onSave(holidayList);
            handleCloseHoliday();
          }}
        >
          Close
        </Button>
      </DialogActions>

      {/* 📝 Form Dialog */}
      <HolidayFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleFormSave}
        holiday={editingHoliday}
        hostelId={hostelId}
      />

      {/* 🗑️ Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirmed}
        title="Delete Holiday / Closure"
        message={`Are you sure you want to delete this ${
          deleteTarget?.scheduleType === "HOLIDAY"
            ? "Holiday"
            : deleteTarget?.scheduleType === "CLOSURE"
            ? "Closure"
            : "Record"
        } from ${deleteTarget?.startDate || "-"} to ${
          deleteTarget?.endDate || "-"
        }?`}
      />
    </Dialog>
  );
};

export default HolidayDialog;
